import { useCallback, useEffect, useRef, useState } from 'react';
import { httpClient } from '../api/httpClient';
import { toast } from 'react-toastify';

export const usePost = (endpoint) => {
    const [loading, setLoading] = useState(false);

    const postData = async (payload) => {
        setLoading(true);
        try {
            console.debug("[POST: " + endpoint + "]: Payload", payload);
            let { data } = await httpClient.post(endpoint, payload);
            console.debug("[POST: " + endpoint + "]: Response", data);
            if (data?.success) {
                setLoading(false);
                return data?.data;
            }
            else {
                const error = new Error(data?.message || "Request failed");
                error.response = { data };
                throw error;
            }
        } catch (e) {
            console.error("Post Error: ", e);
            const status = e?.response?.status;
            const msg =
                e?.response?.data?.message ??
                (status === 404
                    ? "API route not found (404). Check REACT_APP_API_BASE_URL and deploy Firebase Functions."
                    : null) ??
                "An unknown error occurred";
            toast.error(msg);

            setLoading(false);

            throw e;
        }
    }
    const putData = async (payload, endP) => {
        if (!endP) endP = endpoint;
        setLoading(true);
        try {
            console.debug("[PUT: " + endP + "]: Payload", payload);
            let { data } = await httpClient.put(endP, payload);
            console.debug("[PUT: " + endP + "]: Response", data);
            if (data?.success) {
                setLoading(false);
                return data?.data;
            }
            else {
                throw new Error(data?.message || "Request failed");
            }
        } catch (e) {
            console.error("PUT Error: ", e);
            const status = e?.response?.status;
            const msg =
                e?.response?.data?.message ??
                (status === 404
                    ? "API route not found (404). Check REACT_APP_API_BASE_URL and deploy Firebase Functions."
                    : null) ??
                "An unknown error occurred";
            toast.error(msg);
            setLoading(false);

            throw e;
        }
    }

    return { post: postData, put: putData, loading };
}

export const usePut = (endpoint) => {
    const [loading, setLoading] = useState(false);

    const postData = async (payload) => {
        setLoading(true);
        try {
            let { data } = await httpClient.put(endpoint, payload);
            if (data?.success) {
                setLoading(false);
                return data?.data;
            }
            else throw new Error(data?.message || "Request failed");
        } catch (e) {
            const status = e?.response?.status;
            const msg =
                e?.response?.data?.message ??
                (status === 404
                    ? "API route not found (404). Check REACT_APP_API_BASE_URL and deploy Firebase Functions."
                    : null) ??
                "An unknown error occurred";
            toast.error(msg);
            console.log("Put Error: ", e);
        }
        setLoading(false);
    }

    return { post: postData, loading };
}

export const useGet = (endpoint, loadInstantly = true, query) => {
    const [loading, setLoading] = useState(loadInstantly);
    const [data, setData] = useState({});
    const controllerRef = useRef(null);

    const loadData = useCallback(() => {
        controllerRef.current?.abort?.();
        const controller = new AbortController();
        controllerRef.current = controller;
        setLoading(true);
        httpClient.get(endpoint, {
            signal: controller.signal,
            params: {
                ...query
            },
            paramsSerializer: {
                indexes: null
            }
        })
            .then(res => {
                if (res?.data?.success) {
                    setLoading(false);
                    setData(res?.data?.data);
                }
            })
            .catch((e) => {
                if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
                    setLoading(false);
                    return;
                }
                console.error("GET Error:", e);
                const status = e?.response?.status;
                const msg =
                    e?.response?.data?.message ??
                    (status === 404
                        ? "API route not found (404). Check REACT_APP_API_BASE_URL and deploy Firebase Functions."
                        : null) ??
                    "An unknown error occurred";
                toast.error(msg);
                setLoading(false);
            });
    }, [endpoint, query]);
    useEffect(() => {
        if (loadInstantly) loadData();
    }, [loadData, loadInstantly])
    useEffect(() => () => controllerRef.current?.abort?.(), []);
    return { loading, data, load: loadData };
}

export const useGetPaginated = (endpoint, loadInstantly = true, query) => {
    const [loading, setLoading] = useState(loadInstantly);
    const [data, setData] = useState({});
    const controllerRef = useRef(null);

    const loadData = useCallback((pNo = 1, pSize = 10) => {
        controllerRef.current?.abort?.();
        const controller = new AbortController();
        controllerRef.current = controller;
        setLoading(true);
        httpClient.get(endpoint + (/\?/.test(endpoint) ? "&" : "?") + `PageNo=${pNo}&PageSize=${pSize}`, {
            signal: controller.signal,
            params: {
                ...query
            }
        })
            .then(res => {
                if (res?.data?.success) {
                    setData(res?.data?.data);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
                    setLoading(false);
                    return;
                }
                console.error("GET(Paginated) Error:", e);
                const status = e?.response?.status;
                const msg =
                    e?.response?.data?.message ??
                    (status === 404
                        ? "API route not found (404). Check REACT_APP_API_BASE_URL and deploy Firebase Functions."
                        : null) ??
                    "An unknown error occurred";
                toast.error(msg);
                setLoading(false);
            });
    }, [endpoint, query]);
    const refresh = () => {
        loadData(data?.currentPage, data?.pageSize);
    }

    useEffect(() => {
        if (loadInstantly) loadData();
    }, [loadData, loadInstantly]);
    useEffect(() => () => controllerRef.current?.abort?.(), []);

    return { list: data?.list, refresh, pageNo: data?.currentPage, pageSize: data?.pageSize, totalCount: data?.totalCount, totalPages: data?.totalPages, loading, load: loadData }
}
